import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { useHits, useInstantSearch } from "react-instantsearch";
import dayjs from "dayjs";
import type { AlgoliaAdCampaignObject } from "common/interfaces/Algolia";
import { normalizeCampaignPublishErrors } from "@common/utils/campaignPublishErrors";
import CampaignActiveRow from "./components/CampaignActiveRow";
import CampaignActiveErrorRow from "./components/CampaignActiveErrorRow";

const TABLE_GRID_TEMPLATE = "64px 1fr 160px 120px 120px 160px";

export type CampaignsTableHitsProps = {
  /** When false, the status/performance dot is not shown (e.g. Past Campaigns table). */
  showPerformanceIndicator?: boolean;
  /** When true, subtitle shows "Ended today" / "Ended X days ago" instead of launched (e.g. Past Campaigns table). */
  isPastCampaigns?: boolean;
};

function isScheduledActiveCampaign(row: AlgoliaAdCampaignObject): boolean {
  if (row.status !== "ACTIVE" || !row.schedule_start_at) return false;
  const start = dayjs(row.schedule_start_at);
  return start.isValid() && start.isAfter(dayjs());
}

function formatScheduledStartLabel(startAt: string): string {
  const start = dayjs(startAt);
  if (!start.isValid()) return "Scheduled";
  const month = start.format("MMMM");
  const day = start.format("D");
  const year = start.format("YY");
  const hour = start.format("h");
  const minute = start.format("mm");
  const ampm = start.format("A");
  const tzLabel = start.format("z").toUpperCase();

  return `Scheduled: ${month} ${day} '${year} • ${hour}:${minute}${ampm} ${tzLabel}`;
}

function campaignSubtitle(
  row: AlgoliaAdCampaignObject,
  isPastCampaigns?: boolean
): string {
  const now = dayjs();
  const start = row.schedule_start_at ? dayjs(row.schedule_start_at) : null;
  const end = row.schedule_end_at ? dayjs(row.schedule_end_at) : null;
  if (isPastCampaigns && end?.isValid()) {
    const todayEnd = now.startOf("day");
    const endDay = end.startOf("day");
    const daysAgo = todayEnd.diff(endDay, "day");
    if (daysAgo === 0) return "Ended today";
    if (daysAgo === 1) return "Ended 1 day ago";
    return `Ended ${daysAgo} days ago`;
  }

  if (
    !isPastCampaigns &&
    row.schedule_start_at &&
    isScheduledActiveCampaign(row)
  ) {
    return formatScheduledStartLabel(row.schedule_start_at);
  }

  const parts: string[] = [];
  if (row.status === "PAUSED") {
    parts.push("Paused");
  }

  if (start?.isValid() && !start.isAfter(now)) {
    const todayStart = now.startOf("day");
    const startDay = start.startOf("day");
    const daysAgo = todayStart.diff(startDay, "day");
    if (daysAgo === 0) {
      parts.push("Launched today");
    } else {
      parts.push(`Launched ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`);
    }
  }

  if (end?.isValid() && end.isAfter(now)) {
    const daysLeft = end.diff(now, "day");
    parts.push(`${daysLeft} day${daysLeft === 1 ? "" : "s"} left`);
  }

  if (parts.length > 0) {
    return parts.join(" • ");
  }

  if (row.status === "FROZEN") return "Frozen";
  if (row.status && row.status.trim().length > 0) {
    const prettyStatus = row.status
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
    return prettyStatus;
  }

  return "Not published";
}

function eventDateLabel(row: AlgoliaAdCampaignObject): string | undefined {
  const eventDate = row.event_start_at ?? row.schedule_start_at;
  if (!eventDate) return undefined;
  const parsed = dayjs(eventDate);
  if (!parsed.isValid()) return undefined;
  return `Event date: ${parsed.format("MMMM D 'YY")}`;
}

export default function CampaignsTableHits({
  showPerformanceIndicator = true,
  isPastCampaigns = false,
}: CampaignsTableHitsProps = {}) {
  const { status } = useInstantSearch();
  const { items: hits } = useHits<AlgoliaAdCampaignObject>();

  if (status === "loading") {
    return (
      <VStack py={6}>
        <Spinner />
      </VStack>
    );
  }

  if (!hits.length) {
    return (
      <Text color="gray.500" py={4} px={4}>
        No campaigns match your search.
      </Text>
    );
  }

  return (
    <Box minW="min-content">
      <VStack
        align="stretch"
        spacing={0}
        divider={<Box h="1px" bg="gray.100" />}
        minW={{ base: "640px", md: "auto" }}
      >
        {hits.map((row) => {
          const subtitle = campaignSubtitle(row, isPastCampaigns);
          const publishErrors = normalizeCampaignPublishErrors(
            row.publish_errors
          );
          const hasPublishErrors =
            showPerformanceIndicator === true && publishErrors.length > 0;

          if (hasPublishErrors) {
            return (
              <CampaignActiveErrorRow
                key={row.objectID}
                campaignId={row.objectID}
                campaignName={row.name}
                publishErrors={publishErrors}
                eventDateLabel={eventDateLabel(row)}
                imageUrl={row.image_url}
                tableGridTemplate={TABLE_GRID_TEMPLATE}
              />
            );
          }

          return (
            <CampaignActiveRow
              key={row.objectID}
              row={row}
              subtitle={subtitle}
              eventDateLabel={eventDateLabel(row)}
              status={
                row.status === "ACTIVE" && isScheduledActiveCampaign(row)
                  ? "SCHEDULED"
                  : row.status
              }
              showPerformanceIndicator={showPerformanceIndicator}
              tableGridTemplate={TABLE_GRID_TEMPLATE}
            />
          );
        })}
      </VStack>
    </Box>
  );
}
